import { disableNetwork, enableNetwork } from 'firebase/firestore';
import { db } from '../firebase';
import { trace } from './traceLog';

/**
 * Drop and rebuild the Firestore client's network transport.
 *
 * This is the shipped remedy for the iOS WKWebView wedge (see the resume
 * handler note in App.js): after the WebView is suspended, Firestore's
 * long-polling streams can die silently, leaving reads/writes hanging with no
 * error. Cycling the network forces fresh streams.
 *
 * Why this lives in one place instead of inline call sites:
 * - The toggle is GLOBAL to the Firestore client. Every live listener in the
 *   app rides through it, so the call sites (App.js resume handler, OfferPage
 *   retry button) must not interleave independent disable/enable sequences.
 *   Concurrent callers coalesce onto the same in-flight cycle.
 * - Both steps are BOUNDED (2026-09-23 audit finding): on a wedged client,
 *   disableNetwork itself can fail to settle, and an unbounded await here
 *   left the shared in-flight promise pending forever, silently bricking
 *   every future reconnect attempt, including the user-facing Retry button.
 *   A timed-out step resolves this cycle instead; the underlying SDK promise
 *   may still settle later on its own, which is harmless.
 * - enableNetwork is always REQUESTED, even after a failed or timed-out
 *   disable: a redundant enable is a no-op, while skipping it risks
 *   stranding the whole client offline. Requested is not the same as run:
 *   the SDK executes disable and enable one after the other on a single
 *   internal queue (@firebase/firestore 4.7.3: both asyncQueue.enqueue), so
 *   if disable hangs, enable waits behind it. A timeout here resolves OUR
 *   promise; it cancels nothing and does not mean networking is enabled.
 *
 * - A cycle whose SDK calls are STILL PENDING after our timeout blocks new
 *   cycles (2026-10-06 review finding): releasing the guard on timeout let
 *   later retries queue more disable/enable pairs behind a stuck operation,
 *   all of which would run back to back when it resumed. Every SDK promise
 *   is tracked until it actually settles; while any is pending, a new call
 *   returns { outcome: 'blocked' } immediately and requests nothing. If the
 *   SDK never settles, every later call stays blocked: the caller keeps its
 *   failure UI and offers Reload; we never escalate to terminate().
 * - disable and enable are each guarded on their own, so a SYNCHRONOUS throw
 *   from disableNetwork still leads to enableNetwork being requested.
 *
 * Resolves (never rejects) with a structured outcome:
 *   { outcome: 'recovered' | 'timed_out' | 'failed' | 'blocked', disable, enable }
 * so callers can report truthfully instead of guessing.
 */

const STEP_TIMEOUT_MS = 5000;

// Calls an SDK function, turning a synchronous throw into a rejection so the
// caller's flow (and the enable that must follow a disable) is unaffected.
function requestStep(fn) {
  try {
    return Promise.resolve(fn());
  } catch (e) {
    return Promise.reject(e);
  }
}

function boundedStep(promise) {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve('timed_out'), STEP_TIMEOUT_MS);
    promise.then(
      () => { clearTimeout(timer); resolve('ok'); },
      () => { clearTimeout(timer); resolve('failed'); }
    );
  });
}

let inFlight = null;
// SDK calls (disable/enable) that have not settled yet, even if our bounded
// wait for them has already given up.
const pendingSdkCalls = new Set();

function track(promise) {
  const settled = promise.then(() => {}, () => {});
  pendingSdkCalls.add(settled);
  settled.then(() => pendingSdkCalls.delete(settled));
  return promise;
}

export function reconnectFirestore() {
  if (inFlight) return inFlight;
  if (pendingSdkCalls.size > 0) {
    trace('reconnect:blocked', { pending: pendingSdkCalls.size });
    return Promise.resolve({ outcome: 'blocked', disable: 'pending', enable: 'pending' });
  }
  inFlight = (async () => {
    trace('reconnect:start');
    try {
      const disable = await boundedStep(track(requestStep(() => disableNetwork(db))));
      const enable = await boundedStep(track(requestStep(() => enableNetwork(db))));
      const outcome =
        disable === 'ok' && enable === 'ok' ? 'recovered'
          : disable === 'timed_out' || enable === 'timed_out' ? 'timed_out'
            : 'failed';
      trace('reconnect:end', { outcome, disable, enable });
      return { outcome, disable, enable };
    } finally {
      // Our wait is over; whether a NEW cycle may start is decided by
      // pendingSdkCalls, not by this flag.
      inFlight = null;
    }
  })();
  return inFlight;
}
